/**
 * The public blog: release posts and articles at one route, newest first, with
 * Everything / Releases / Articles tabs (`?kind=`), search (`?q=`) and the
 * subscribe form. Each post has its own page at /blog/<slug>, and the feed is
 * /blog/rss.xml. Spec agent-app/blog (OpenSpec change automate-releases).
 *
 * /changelog and /release-notes redirect here.
 */

import crypto from "node:crypto";
import {
  json,
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  type LinksFunction,
} from "@remix-run/node";
import {
  useLoaderData,
  useFetcher,
  Form as RemixForm,
  Link,
} from "@remix-run/react";
import { Search, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Footer from "#app/components/footer";
import { LandingHeader } from "#app/components/landing/landing-header";
import { ChangelogSubscriptionConfirmationEmail } from "#app/email-templates/changelog-subscription-confirmation";
import { useTheme } from "#app/routes/resources+/theme-switch.tsx";
import { cleanPostTitle, renderPostContent } from "#app/utils/blog-content";
import { isPostKind } from "#app/utils/blog.server";
import { prisma } from "#app/utils/db.server";
import { sendEmail } from "#app/utils/email.server";
import { sanitizeHtml } from "#app/utils/sanitize-html";
import "quill/dist/quill.snow.css";

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get("intent");

  if (intent === "subscribe") {
    const email = formData.get("email") as string;
    if (!email || !email.includes("@")) {
      return json({ error: "Invalid email" }, { status: 400 });
    }

    try {
      const subscriber = await prisma.changelogSubscriber.upsert({
        where: { email },
        update: { isActive: true },
        create: { email },
      });

      // Backfill unsubscribe token for older rows (if schema was added later)
      let unsubscribeToken = (subscriber as any).unsubscribeToken;
      if (!unsubscribeToken) {
        unsubscribeToken = crypto.randomUUID();
        await prisma.changelogSubscriber.update({
          where: { id: subscriber.id },
          data: { unsubscribeToken },
        });
      }

      // Ensure default list exists and subscribe user to it
      const defaultListName = "Product Updates";
      const emailListDelegate = (prisma as any).changelogEmailList;
      const emailSubDelegate = (prisma as any).changelogEmailSubscription;
      if (emailListDelegate?.upsert && emailSubDelegate?.upsert) {
        const defaultList = await emailListDelegate.upsert({
          where: { id: "default-product-updates" },
          update: {},
          create: {
            id: "default-product-updates",
            name: defaultListName,
            type: "monthly_publish",
            enabled: true,
          },
        });

        await emailSubDelegate.upsert({
          where: {
            listId_subscriberId: {
              listId: defaultList.id,
              subscriberId: subscriber.id,
            },
          },
          update: {},
          create: {
            listId: defaultList.id,
            subscriberId: subscriber.id,
          },
        });
      }

      // Send confirmation email
      const appOrigin = process.env.APP_ORIGIN_URL || "http://localhost:3000";
      const unsubscribeUrl = `${appOrigin}/blog/unsubscribe?token=${unsubscribeToken}`;
      const changelogUrl = `${appOrigin}/blog`;

      const emailResult = await sendEmail({
        to: email,
        subject: "You're subscribed to Supervity Changelog Updates",
        react: (
          <ChangelogSubscriptionConfirmationEmail
            unsubscribeUrl={unsubscribeUrl}
            changelogUrl={changelogUrl}
          />
        ),
      });

      if (emailResult.status === "error") {
        console.error("Failed to send confirmation email:", emailResult.error);
        // Still return success since subscription was saved
      }

      return json({ success: true, message: "Subscribed successfully!" });
    } catch {
      return json({ error: "Subscription failed" }, { status: 500 });
    }
  }
  return json({});
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const search = url.searchParams.get("q")?.toLowerCase() || "";
  const kindParam = url.searchParams.get("kind");
  const kind = isPostKind(kindParam) ? kindParam : "all";

  // Fetch all published entries
  const entries = await prisma.changelogEntry.findMany({
    where: {
      status: "published",
      ...(kind !== "all" ? { kind } : {}),
    },
    // Newest publication first, as in the feed. Entries from before
    // publishedAt existed have none, and sort by their date after the rest.
    orderBy: [
      { publishedAt: { sort: "desc", nulls: "last" } },
      { date: "desc" },
    ],
  });

  // Client-side filtering for simple search
  const filteredEntries = search
    ? entries.filter(
        (e) =>
          (e.title || "").toLowerCase().includes(search) ||
          (e.summary || "").toLowerCase().includes(search) ||
          (e.description || "").toLowerCase().includes(search),
      )
    : entries;

  // Group by Month
  const grouped = new Map<string, { period: string; items: typeof entries }>();
  for (const e of filteredEntries) {
    const month = new Date(e.publishedAt ?? e.date).toLocaleDateString(
      "en-US",
      {
        month: "long",
        year: "numeric",
      },
    );
    const groupId = month;
    const period = month;

    if (!grouped.has(groupId)) {
      grouped.set(groupId, { period, items: [] });
    }
    grouped.get(groupId)!.items.push(e);
  }

  const timeline = Array.from(grouped.values());

  return json({
    timeline,
    hasData: filteredEntries.length > 0,
    search,
    kind,
  });
}

export const links: LinksFunction = () => [
  {
    rel: "alternate",
    type: "application/rss+xml",
    title: "Supervity blog",
    href: "/blog/rss.xml",
  },
];

export const handle = {
  fullBleed: true,
  i18n: ["marketing", "footer", "common"],
};

export default function BlogPage() {
  const { t, i18n } = useTranslation("marketing");
  const isJapanese = i18n.resolvedLanguage?.startsWith("ja") ?? false;
  const { timeline, hasData, search, kind } = useLoaderData<typeof loader>();
  const tabHref = (k: "all" | "release" | "article") => {
    const params = new URLSearchParams();
    if (k !== "all") params.set("kind", k);
    if (search) params.set("q", search);
    const qs = params.toString();
    return qs ? `/blog?${qs}` : "/blog";
  };
  const _theme = useTheme();
  const fetcher = useFetcher();
  const [query, setQuery] = useState(search);

  const getCategoryBadge = (cat: string) => {
    const colors: Record<string, string> = {
      Added:
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      Fixed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      Changed:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      Removed:
        "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
    };
    return (
      colors[cat] ||
      "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
    );
  };

  const getCategoryIcon = (cat: string) => {
    const icons: Record<string, string> = {
      Added: "✨",
      Fixed: "🐛",
      Changed: "🚀",
      Removed: "🗑️",
    };
    return icons[cat] || "📌";
  };

  const renderContent = renderPostContent;
  const cleanTitle = cleanPostTitle;

  const getLocalizedEntry = (item: any) => {
    const sourceTitle = cleanTitle(item.title || "").replace(/<[^>]*>/g, "");
    if (
      !isJapanese ||
      item.version !== "v1.0.0" ||
      !sourceTitle.includes("Supervity Platform")
    ) {
      return {
        title: item.title || "",
        description: item.enhancedDescription || item.description || "",
      };
    }

    return {
      title: "Supervity Platform — エンタープライズ向けリリースノート",
      description: `
        <h2>Auto AI Employees™ のご紹介 🚀</h2>
        <p>Supervity Platform初の主要エンタープライズリリースとして、<strong>Auto AI Employees™</strong>をご紹介します。自然言語を使ってエンタープライズ業務を自動化する、インテリジェントなデジタルワーカーの新しいカテゴリです。</p>
        <p>Auto AI Employees™は、指示の理解、ワークフローの実行、チームとの連携、エンタープライズシステムとの対話、ドキュメント処理、組織機能をまたぐ継続的な業務遂行が可能な、自律型かつタスク指向のAIワーカーです。人による監督とガバナンスを常に維持します。</p>
        <p>本リリースは、エンタープライズ規模のAIワークフォース自動化に向けた運用基盤を確立します。</p>
        <h3>主な機能</h3>
        <ul>
          <li>自然言語による業務指示とタスク実行</li>
          <li>既存のエンタープライズシステムおよびワークフローとの安全な連携</li>
          <li>人による承認、監督、監査を組み込んだガバナンス</li>
          <li>組織全体で再利用できる、拡張可能なAIワーカー基盤</li>
        </ul>
      `,
    };
  };

  const ChangelogItem = ({
    item,
    isLatest,
  }: {
    item: any;
    isLatest: boolean;
  }) => {
    const [isOpen, setIsOpen] = useState(isLatest);
    const localizedItem = getLocalizedEntry(item);

    return (
      <details
        className="group bg-card border rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-all duration-200"
        open={isOpen}
      >
        <summary
          className="list-none [&::-webkit-details-marker]:hidden cursor-pointer outline-none select-none relative pb-0 group-open:pb-6 border-b-0 group-open:border-b border-border transition-all"
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
        >
          <div className="absolute right-2 top-1/2 -translate-y-1/2 transition-transform duration-200 group-open:rotate-180">
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          </div>

          <div className="pr-10">
            {/* Title row */}
            <div className="flex items-start gap-3 mb-3">
              <span className="text-2xl shrink-0 mt-0.5">
                {getCategoryIcon(
                  (item.category || "").split(",")[0] || "Added",
                )}
              </span>
              <div
                className="release-note-title min-w-0 text-2xl  font-bold text-[#141A42] dark:text-foreground leading-snug [&_p]:m-0 [&_p]:inline"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    cleanTitle(localizedItem.title).replace(
                      /&nbsp;|\u00A0/g,
                      " ",
                    ),
                  ),
                }}
              />
            </div>

            {/* Kind, version and category badges */}
            <div className="flex flex-wrap items-center gap-2 sm:ml-9">
              {item.kind === "article" && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-muted text-muted-foreground border">
                  {t("changelog.kindArticle")}
                </span>
              )}
              {item.version && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#F0F4FF] text-[#3B5998] border border-[#D0DDF7] dark:bg-blue-900 dark:text-blue-200 dark:border-blue-800">
                  {item.version}
                </span>
              )}
              {(item.category || "Added")
                .split(",")
                .map((cat: string, i: number) => (
                  <span
                    key={i}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryBadge(cat)}`}
                  >
                    {t(`changelog.cat${cat}`, { defaultValue: cat })}
                  </span>
                ))}
            </div>

            {item.summary && (
              <p className="mt-3 sm:ml-9 text-base leading-relaxed text-[#5C67AA] dark:text-indigo-300">
                {item.summary}
              </p>
            )}
          </div>
        </summary>

        {/* Body — rendered exactly as in the Quill editor */}
        <div className="ql-snow changelog-content pt-6 animate-in slide-in-from-top-2 duration-200 sm:ml-9">
          {/* The permalink lives here, not in <summary>: the summary is the
              card's toggle, and a link inside it is a control nested in a
              control (axe nested-interactive). */}
          {item.slug && (
            <p className="mb-4">
              <Link
                to={`/blog/${item.slug}`}
                className="text-xs font-semibold text-primary underline-offset-4 hover:underline"
              >
                {t("changelog.permalink")}
              </Link>
            </p>
          )}
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                renderContent(localizedItem.description).replace(
                  /&nbsp;|\u00A0/g,
                  " ",
                ),
              ),
            }}
            className="ql-editor p-0! min-h-0! text-[#141A42] dark:text-foreground w-full"
            style={{ lineHeight: "1.75" }}
          />
        </div>
      </details>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:bg-zinc-950">
      <LandingHeader
        showLogin={true}
        backTo="/"
        backLabel={t("legal.backToHome")}
      />

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-[url('/img/marketplace-bg.svg')] px-4 py-16 border-b border-[#E8EBF6] dark:border-zinc-800 dark:bg-zinc-950 dark:bg-none">
        <div className="absolute inset-0 hidden bg-zinc-950/80 dark:block" />
        <div className="absolute right-0 top-0 h-full w-[45%] bg-[radial-gradient(circle_at_center,rgba(128,149,212,0.24)_0%,rgba(128,149,212,0.08)_35%,rgba(255,255,255,0)_70%)] dark:bg-[radial-gradient(circle_at_center,rgba(128,149,212,0.18)_0%,rgba(24,24,27,0)_70%)]" />
        <div className="relative container mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D8DDF0] bg-white/80 px-4 py-1.5 text-sm text-[#5C67AA] mb-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-indigo-300">
            <span className="text-base">🚀</span>
            {t("changelog.badge")}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#141A42] dark:text-zinc-100 mb-4">
            {t("changelog.title")}
          </h1>
          <p className="text-[#5C67AA] text-lg max-w-2xl mx-auto dark:text-indigo-300">
            {t("changelog.subtitle")}
          </p>
        </div>
      </div>

      {/* Sticky Search & Filters */}
      <div className="sticky top-0 z-40 bg-[#FDFDFD]/80 dark:bg-zinc-950/80 backdrop-blur-md border-b border-[#E8EBF6] dark:border-zinc-800 py-4">
        <div className="container mx-auto px-4 max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <nav aria-label={t("changelog.tabsLabel")} className="flex gap-1">
              {(
                [
                  ["all", t("changelog.tabEverything")],
                  ["release", t("changelog.tabReleases")],
                  ["article", t("changelog.tabArticles")],
                ] as const
              ).map(([k, label]) => (
                <Link
                  key={k}
                  to={tabHref(k)}
                  aria-current={kind === k ? "page" : undefined}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                    kind === k
                      ? "bg-[#141A42] text-white dark:bg-zinc-100 dark:text-zinc-900"
                      : "text-[#5C67AA] hover:bg-muted dark:text-indigo-300"
                  }`}
                >
                  {label}
                </Link>
              ))}
            </nav>
          </div>
          <RemixForm
            method="get"
            className="flex items-center gap-3 w-full sm:w-auto"
          >
            {kind !== "all" && <input type="hidden" name="kind" value={kind} />}
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E96BB]" />
              <input
                type="text"
                name="q"
                placeholder={t("changelog.searchPlaceholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-xl border border-[#E0E4F4] bg-white dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-[#535EA4]/20 focus:border-[#535EA4] transition-all"
              />
            </div>
          </RemixForm>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {" "}
        {!hasData ? (
          <div className="text-center py-20 bg-muted/30 rounded-xl border border-dashed">
            <h3 className="text-2xl font-semibold mb-2">
              {t("changelog.noUpdates")}
            </h3>
            <p className="text-muted-foreground">
              {t("changelog.noUpdatesHint")}
            </p>
          </div>
        ) : (
          <div className="space-y-16">
            {timeline.map((group, groupIdx) => (
              <div key={groupIdx} className="relative mb-16">
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-3xl font-bold border-b pb-2 grow">
                    {new Intl.DateTimeFormat(i18n.resolvedLanguage || "en", {
                      month: "long",
                      year: "numeric",
                    }).format(
                      new Date(
                        group.items[0]?.publishedAt ??
                          group.items[0]?.date ??
                          group.period,
                      ),
                    )}
                  </h2>
                </div>

                <div className="space-y-8">
                  {group.items.map((item: any, itemIdx: number) => (
                    <ChangelogItem
                      key={itemIdx}
                      item={item}
                      isLatest={itemIdx === 0 && groupIdx === 0}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Footer */}
        <div className="mt-16 pt-12 border-t border-border">
          {/* Subscription Form */}
          <div className="max-w-md mx-auto mb-12 text-center bg-muted/30 p-8 rounded-2xl border border-border">
            <h3 className="text-xl font-bold mb-2">
              {t("changelog.subscribeTitle")}
            </h3>
            <p className="text-muted-foreground text-sm mb-6">
              {t("changelog.subscribeHint")}
            </p>
            <fetcher.Form
              method="post"
              className="flex flex-col sm:flex-row gap-2"
            >
              <input type="hidden" name="intent" value="subscribe" />
              <input
                type="email"
                name="email"
                placeholder={t("changelog.emailPlaceholder")}
                required
                className="flex-1 px-4 py-2 rounded-md border border-input bg-background text-sm"
              />
              <button
                type="submit"
                className="flex items-center justify-center rounded-[10.44px] px-6 py-2.5 text-sm font-bold leading-[130%] tracking-[0.03em] capitalize text-[#D7DDF7] transition-opacity hover:opacity-95 focus-visible:ring-2 focus-visible:ring-white/30 bg-[linear-gradient(93.53deg,#141A42_14.58%,#6577A4_110.19%)] disabled:opacity-50"
                disabled={fetcher.state !== "idle"}
              >
                {fetcher.state !== "idle"
                  ? t("contact.sending")
                  : t("changelog.subscribe")}
              </button>
            </fetcher.Form>
            {fetcher.data && (fetcher.data as any).success && (
              <p className="text-green-600 dark:text-green-300 text-sm mt-3 font-medium">
                ✨ {t("changelog.subscribed")}
              </p>
            )}
            {fetcher.data && (fetcher.data as any).error && (
              <p className="text-red-600 dark:text-red-300 text-sm mt-3 font-medium">
                {(fetcher.data as any).error}
              </p>
            )}
          </div>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {t("changelog.poweredBy")}{" "}
              <a
                href="https://supervity.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground hover:text-primary transition-colors"
              >
                Supervity
              </a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
